import { useState, useEffect } from 'react';
import { IApiContentResponseModel, NotificationPreferenceDto } from '@/services/platform/api/model';
import { useUpdateNotifications } from '@/services/platform/data/profile/notifications';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { Switch } from '@/components/ui/switch';
import { useClientTranslation } from '@/lib/core';

interface EditContactSettingsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  notifications: NotificationPreferenceDto[];
  items: IApiContentResponseModel[];
  onSaved: () => void;
}

export function EditContactSettingsSheet({
  open,
  onOpenChange,
  notifications,
  items,
  onSaved,
}: EditContactSettingsSheetProps) {
  const t = useClientTranslation();
  const { mutateAsync: updateNotifications, isPending } = useUpdateNotifications();

  const [draft, setDraft] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (open) {
      const initial: Record<string, boolean> = {};
      for (const n of notifications) {
        if (n.notificationTypeNodeKey) {
          initial[n.notificationTypeNodeKey] = n.isEnabled ?? false;
        }
      }
      setDraft(initial);
    }
  }, [open, notifications]);

  function toggle(nodeKey: string) {
    setDraft(prev => ({ ...prev, [nodeKey]: !prev[nodeKey] }));
  }

  async function handleSave() {
    await updateNotifications({
      data: {
        notifications: Object.entries(draft).map(([notificationTypeNodeKey, isEnabled]) => ({
          notificationTypeNodeKey,
          isEnabled,
        })),
      },
    });
    onSaved();
    onOpenChange(false);
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="flex flex-col gap-0 px-0 pb-0">
        {/* Header */}
        <div className="flex items-center justify-center px-4 pt-4 pb-4">
          <p className="font-pp-neue-montreal text-[14px] leading-3.5 tracking-[0.5px] text-[#1a1a1a] uppercase">
            {t('App.Labels.EditContactSettings')}
          </p>
        </div>

        {/* Items */}
        <div className="flex flex-col gap-4 px-4 pt-4 pb-10">
          {items.map(item => {
            const nodeKey = item.id ?? '';
            const label = item.name ?? '';
            return (
              <Switch
                key={nodeKey}
                checked={draft[nodeKey] ?? false}
                onCheckedChange={() => toggle(nodeKey)}
                wrapperClassName="items-center"
                label={
                  <span className="font-pp-neue-montreal min-w-0 flex-1 text-[14px] leading-3.5 font-medium tracking-[0.5px] text-[#1a1a1a]">
                    {label}
                  </span>
                }
              />
            );
          })}
        </div>

        {/* Footer */}
        <div className="mt-auto flex flex-col gap-1 border-t-[0.5px] border-[#c8c8c8] px-4 pt-6 pb-10.5">
          <button
            type="button"
            disabled={isPending}
            onClick={handleSave}
            className="font-pp-neue-montreal h-11.5 w-full rounded-[2px] bg-[#1a1a1a] px-4 text-[12px] font-semibold tracking-[0.5px] text-white uppercase disabled:opacity-60"
          >
            {t('App.Labels.SaveChanges')}
          </button>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="font-pp-neue-montreal h-9 w-full text-center text-[12px] tracking-[0.5px] text-[#1a1a1a] uppercase"
          >
            {t('App.Labels.Cancel')}
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
